/* eslint-disable no-nested-ternary */
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Pagination,
  Paper,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import * as yup from 'yup'
import { ReactElement, useEffect, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  fetchCourses,
  selectCoursesStatus,
  selectCourses,
  fetchSeries,
  selectSeriesStatus,
  selectSeries,
} from 'src/v3/create/create.slice'

import {
  AddIcon,
  AddImageIcon,
  CancelIcon,
  CorrectIcon,
  DeleteIcon,
  EditIcon,
  ImageBannerIcon,
  PreviewIcon,
  SubmitIcon,
} from 'src/v3/core/icons'
import { Course, Series } from 'src/v3/core/types'
import { useFormik } from 'formik'
import axios from 'src/utilities/http-client'
import theme from 'src/theme'
import EntityList, {
  SeriesClassList,
} from 'src/v3/manage/components/entity-list'
import CourseCard from 'src/v3/manage/components/course-card'
import noop from 'src/utilities/noop'
import { processImageFile, uploadImageToS3 } from 'src/v3/utilities/s3'
import ConfirmAction from 'src/v3/components/confirm-action'
import Loading from 'src/v3/components/loading'

export default (): ReactElement => {
  let dispatch = useAppDispatch()

  let courses = useAppSelector(selectCourses)
  let coursesStatus = useAppSelector(selectCoursesStatus)
  let seriesStatus = useAppSelector(selectSeriesStatus)
  let series: Series[] = useAppSelector(selectSeries)

  let [action, setAction] = useState<'create' | 'view' | 'edit' | 'delete'>(
    'view',
  )
  let [saving, setSaving] = useState<boolean>(false)
  let [checkedCourses, setCheckedCourses] = useState<string[]>([])
  let [selected, setSelected] = useState<string>('')
  let [page, setPage] = useState<number>(1)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  let [uploading, setUploading] = useState<string>('')
  let [errorMessage, setErrorMessage] = useState<string>('')
  let selectedSeries = series.find((s) => s.id === selected)
  let [episodeAssignments, setEpisodeAssignments] = useState<
    { courseID: string; episode: number; season: number }[]
  >([])

  useEffect(() => {
    if (coursesStatus === 'uninitialized') {
      dispatch(fetchCourses())
    }
  }, [coursesStatus])

  useEffect(() => {
    if (seriesStatus === 'uninitialized') {
      dispatch(fetchSeries())
    }
  }, [seriesStatus])

  useEffect(() => {
    setEpisodeAssignments(
      selectedSeries?.courses.map((x) => ({
        courseID: x.courseID,
        episode: x.episode,
        season: x.season,
      })) || [],
    )
  }, [selectedSeries?.courses])

  let form = useFormik({
    validationSchema: yup.object({
      name: yup.string().required(),
      description: yup.string().required(),
      sequence: yup.number().required(),
    }),
    enableReinitialize: true,
    initialValues: {
      id: '',
      name: '',
      description: '',
      sequence: 0,
    },
    onSubmit: (values) => {
      setSaving(true)
      if (!values.id) {
        axios
          .post('/api/v2/create/series', {
            name: values.name,
            description: values.description,
            sequence: values.sequence,
            courses: checkedCourses,
          })
          .then(() => dispatch(fetchSeries()))
          .then(() => setAction('view'))
          .then(() => setSaving(false))
          .then(() => form.resetForm())
          .catch((err) => {
            setErrorMessage(err?.message || 'Error Creating Series')
            dispatch(fetchSeries())
            setAction('view')
            setSaving(false)
          })
      } else {
        axios
          .put('/api/v2/create/series', {
            id: values.id,
            name: values.name,
            description: values.description,
            sequence: values.sequence,
            courses: checkedCourses,
          })
          .then(() => dispatch(fetchSeries()))
          .then(() => setAction('view'))
          .then(() => setSaving(false))
          .then(() => form.resetForm())
          .catch((err) => {
            setErrorMessage(err?.message || 'Error Updating Series')
            dispatch(fetchSeries())
            setAction('view')
            setSaving(false)
          })
      }
    },
  })

  let publishSeries = () => {
    axios
      .put(`/api/v2/create/series/publish`, {
        id: selected,
        newPublicationStatus:
          selectedSeries?.published === 'unpublished'
            ? 'published'
            : 'unpublished',
      })
      .then(() => dispatch(fetchSeries()))
      .catch((err) => {
        setErrorMessage(err?.message || 'Error Publishing Series')
        dispatch(fetchSeries())
      })
  }

  let onThumbnailUpload = (id: string, t: any) => {
    setUploading(id)
    return axios
      .put('/api/v2/create/series/thumbnail', { id })
      .then((response) => uploadImageToS3(t, response.data))
      .then(() => setUploading(''))
      .catch((err) => {
        setUploading('')
        setErrorMessage(err?.message || 'Error Uploading Thumbnail')
      })
  }

  let onBannerUpload = (id: string, t: any) => {
    setUploading(id)
    return axios
      .put('/api/v2/create/series/banner', { id })
      .then((response) => uploadImageToS3(t, response.data))
      .then(() => setUploading(''))
      .catch((err) => {
        setUploading('')
        setErrorMessage(err?.message || 'Error Uploading Banner')
      })
  }

  let updateSeriesCourse = (
    courseID: string,
    episode: number,
    season: number,
  ) => {
    if (courseID && episode && season) {
      axios
        .put('/api/v2/create/series/course/assignments', {
          seriesID: selectedSeries?.id ?? '',
          courseID,
          episode,
          season,
        })
        .then(() => dispatch(fetchSeries()))
        .catch((err) => {
          setErrorMessage(err?.message || 'Error Updating Series Courses')
          dispatch(fetchSeries())
        })
    }
  }

  let onSelection = (status: typeof action, id: string) => {
    let s = series.find((sc) => sc.id === id)
    form.setFieldValue('id', s?.id || '')
    form.setFieldValue('name', s?.name || '')
    form.setFieldValue('description', s?.description || '')
    form.setFieldValue('sequence', s?.sequence || 0)
    setCheckedCourses(s?.courses.map((x) => x.courseID) || [])
    setSelected(id)
    setAction(status)
  }

  return (
    <>
      <Container maxWidth="xl">
        <Typography variant="h1">Series</Typography>
        <Typography paragraph className="ml-3 d-inline">
          Create an episodic series of courses that are related to each other,
          arranged into seasons.
        </Typography>
        <Box
          sx={{ display: 'flex', flexWrap: 'wrap', rowGap: 2, columnGap: 3 }}
        >
          <Box sx={{ flex: '1 0 320px' }}>
            <Box sx={{ mb: 1.5 }}>
              <Button
                startIcon={<AddIcon />}
                variant="contained"
                onClick={() => onSelection('create', '')}
              >
                Create Series
              </Button>
            </Box>
            {series.map((s) => (
              <Paper
                key={s.id}
                sx={{
                  px: 2,
                  py: 1.5,
                  mb: 1,
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
                onClick={() => onSelection('view', s.id)}
              >
                <Typography>{s.name}</Typography>
                <Typography color="text.secondary">
                  {s.courses.map((x) => x.courseID)?.length || 'No'} Courses
                </Typography>
              </Paper>
            ))}
            <Box sx={{ mt: -1 }}>
              {series.length ? (
                <Pagination
                  size="small"
                  sx={{ py: '5px', display: 'flex', justifyContent: 'center' }}
                  count={Math.ceil(series.length / 10)}
                  page={page}
                  onChange={(e: React.ChangeEvent<unknown>, value: number) => {
                    setPage(value)
                  }}
                  showFirstButton
                  showLastButton
                />
              ) : (
                <Typography sx={{ textAlign: 'center', pt: 4 }}>
                  There are no series to display.
                </Typography>
              )}
            </Box>
          </Box>
          <Box sx={{ flex: '1 0 320px' }}>
            <form onSubmit={form.handleSubmit}>
              {selected || action === 'create' ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Typography textAlign="center" variant="h5">
                    {action === 'create'
                      ? 'Create Series'
                      : selectedSeries?.name}
                  </Typography>
                  {action === 'view' && (
                    <Typography textAlign="center" variant="body1">
                      {selectedSeries?.description}
                    </Typography>
                  )}
                  {action !== 'create' && (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        gap: 1,
                        mt: -1,
                      }}
                    >
                      {action === 'view' || action === 'delete' ? (
                        <Button
                          sx={{ flex: '0 0 120px' }}
                          variant="outlined"
                          color="primary"
                          startIcon={<EditIcon />}
                          onClick={() =>
                            onSelection('edit', selectedSeries?.id || '')
                          }
                        >
                          Edit
                        </Button>
                      ) : (
                        <Button
                          sx={{ flex: '0 0 120px' }}
                          variant="outlined"
                          color="primary"
                          startIcon={<PreviewIcon />}
                          onClick={() =>
                            onSelection('view', selectedSeries?.id || '')
                          }
                        >
                          View
                        </Button>
                      )}
                      <Button
                        sx={{ flex: '0 0 120px' }}
                        variant="contained"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => {
                          setAction('delete')
                        }}
                      >
                        Delete
                      </Button>
                    </Box>
                  )}
                  {action === 'view' && (
                    <Chip
                      sx={{ mt: 1, width: 200, alignSelf: 'center' }}
                      icon={
                        selectedSeries?.published === 'published' ? (
                          <CorrectIcon />
                        ) : (
                          <CancelIcon />
                        )
                      }
                      label={
                        selectedSeries?.published === 'published'
                          ? 'Published'
                          : 'Not Published'
                      }
                      variant={
                        selectedSeries?.published === 'published'
                          ? 'filled'
                          : 'outlined'
                      }
                      color={
                        selectedSeries?.published === 'published'
                          ? 'success'
                          : 'error'
                      }
                    />
                  )}
                  {action === 'view' && selectedSeries?.thumbnail && (
                    <>
                      <Typography textAlign="center" variant="body2">
                        Thumbnail
                      </Typography>
                      <img
                        src={`${import.meta.env.VITE_CLOUDFRONT_URL}/public/${selectedSeries?.thumbnail}`}
                        alt="Thumbnail"
                        style={{ width: '100%', height: 'auto' }}
                      />
                    </>
                  )}
                  {action === 'view' && selectedSeries?.banner && (
                    <>
                      <Typography textAlign="center" variant="body2">
                        Banner
                      </Typography>
                      <img
                        src={`${import.meta.env.VITE_CLOUDFRONT_URL}/public/${selectedSeries?.banner}`}
                        alt="Banner"
                        style={{ width: '100%', height: 'auto' }}
                        className="mb-2"
                      />
                    </>
                  )}
                  {(action === 'create' || action === 'edit') && (
                    <TextField
                      variant="outlined"
                      size="medium"
                      label="Name"
                      name="name"
                      type="text"
                      fullWidth
                      value={form.values.name}
                      onChange={form.handleChange}
                      error={form.touched.name && !!form.errors.name}
                    />
                  )}
                  {(action === 'create' || action === 'edit') && (
                    <TextField
                      multiline
                      variant="outlined"
                      size="medium"
                      label="Description"
                      name="description"
                      type="text"
                      fullWidth
                      value={form.values.description}
                      onChange={form.handleChange}
                      error={
                        form.touched.description && !!form.errors.description
                      }
                    />
                  )}
                  {(action === 'create' || action === 'edit') && (
                    <TextField
                      multiline
                      variant="outlined"
                      size="medium"
                      label="Sequence"
                      name="sequence"
                      type="number"
                      fullWidth
                      value={form.values.sequence}
                      onChange={form.handleChange}
                      error={form.touched.sequence && !!form.errors.sequence}
                    />
                  )}
                  {action === 'edit' && (
                    <Chip
                      sx={{ mt: 1, width: 200, alignSelf: 'center' }}
                      onClick={() => publishSeries()}
                      icon={
                        selectedSeries?.published === 'published' ? (
                          <CorrectIcon />
                        ) : (
                          <CancelIcon />
                        )
                      }
                      label={
                        selectedSeries?.published === 'published'
                          ? 'Published'
                          : 'Not Published'
                      }
                      variant={
                        selectedSeries?.published === 'published'
                          ? 'filled'
                          : 'outlined'
                      }
                      color={
                        selectedSeries?.published === 'published'
                          ? 'success'
                          : 'error'
                      }
                    />
                  )}
                  {uploading !== selectedSeries?.id && action === 'edit' ? (
                    <Tooltip title="Upload Thumbnail">
                      <Button
                        variant="outlined"
                        component="label"
                        sx={{ mt: 1, width: 200, alignSelf: 'center' }}
                      >
                        <AddImageIcon onClick={noop} />
                        <input
                          type="file"
                          accept=".jpg,.jpeg,.png"
                          hidden
                          onChange={(event) =>
                            processImageFile(event, {
                              onLoadingStart: noop,
                              onLoadingEnd: (t: any) => {
                                onThumbnailUpload(selectedSeries?.id ?? '', t)
                              },
                            })
                          }
                        />
                      </Button>
                    </Tooltip>
                  ) : uploading === selectedSeries?.id ? (
                    <CircularProgress size={28} />
                  ) : (
                    <></>
                  )}
                  {uploading !== selectedSeries?.id && action === 'edit' ? (
                    <Tooltip title="Upload Banner">
                      <Button
                        variant="outlined"
                        component="label"
                        sx={{ mt: 1, width: 200, alignSelf: 'center' }}
                      >
                        <ImageBannerIcon onClick={noop} />
                        <input
                          type="file"
                          accept=".jpg,.jpeg,.png"
                          hidden
                          onChange={(event) =>
                            processImageFile(event, {
                              onLoadingStart: noop,
                              onLoadingEnd: (t: any) => {
                                onBannerUpload(selectedSeries?.id ?? '', t)
                              },
                            })
                          }
                        />
                      </Button>
                    </Tooltip>
                  ) : uploading === selectedSeries?.id ? (
                    <CircularProgress size={28} />
                  ) : (
                    <></>
                  )}

                  <Box sx={{ mt: -1, mb: -3 }}>
                    {action === 'view' ? (
                      <SeriesClassList
                        entities={
                          selectedSeries?.courses.map((x) =>
                            courses.find((c) => c.id === x.courseID),
                          ) as Course[]
                        }
                        episodeAssignments={episodeAssignments}
                        setEpisodeAssignments={(episodes) => {
                          setEpisodeAssignments(episodes)
                        }}
                        updateSeriesCourse={updateSeriesCourse}
                        checkbox={undefined}
                      />
                    ) : (
                      <EntityList
                        entities={
                          action === 'create' || action === 'edit'
                            ? courses.filter((c) => !c.linkedServiceID)
                            : courses.filter((c) =>
                                selectedSeries?.courses
                                  .map((x) => x.courseID)
                                  .includes(c.id),
                              )
                        }
                        checkbox={
                          action === 'create' || action === 'edit'
                            ? {
                                checked: checkedCourses,
                                onClick: setCheckedCourses,
                              }
                            : undefined
                        }
                        info={
                          action === 'edit' ? { card: CourseCard } : undefined
                        }
                      />
                    )}
                  </Box>
                  {(action === 'create' || action === 'edit') && (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                      }}
                    >
                      <Button
                        sx={{ flex: '0 0 120px' }}
                        variant="contained"
                        color="primary"
                        size="large"
                        type="submit"
                        startIcon={<SubmitIcon />}
                      >
                        {action === 'create' ? 'Create' : 'Update'}
                      </Button>
                    </Box>
                  )}
                </Box>
              ) : (
                <Typography textAlign="center">
                  Select a series to view details or create a new one.
                </Typography>
              )}
            </form>
          </Box>
        </Box>
        <ConfirmAction
          executeAction={() =>
            axios
              .delete(`/api/v2/create/series?id=${selectedSeries?.id}`)
              .then(() => setSelected(''))
              .then(() => dispatch(fetchSeries()))
          }
          open={action}
          setOpen={(value: typeof action) => {
            setAction(!value ? 'view' : value)
          }}
          openText="delete"
          title="Confirm Deletion"
          text={`Are you sure you want to delete ${selectedSeries?.name}?`}
          onFailure={(err) =>
            setErrorMessage(err.message || 'Error Deleting Series')
          }
        />
        <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
          <Alert
            variant="filled"
            onClose={() => setErrorMessage('')}
            severity="error"
          >
            <strong>Error with request: </strong>
            {errorMessage}
          </Alert>
        </Snackbar>
        <Loading entities={[coursesStatus, seriesStatus]} loading={saving} />
      </Container>
    </>
  )
}
